<script setup lang="ts">
import { computed, inject, onMounted, ref } from "vue";
import { useIsInPdfMode } from "~/stores/pdfModeStore";
import { ElMessage } from "element-plus";
import { cloneDeep, isEqual } from "lodash-es";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";

import {
  DiscussionFormElement,
  FormElementType,
  DiscussionComment,
  UserInfoDto,
  DiscussionAnswerDto,
} from "~/api";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
import DiscussionThreadComment from "./DiscussionThreadComment.vue";
import DiscussionThreadEditor from "./DiscussionThreadEditor.vue";
import { isCommentEmpty } from "./discussionRichText";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";

const props = defineProps<DefaultFormComponentProps<DiscussionFormElement>>();

const myInfoStore = useMyInfoStore();

const { useAnswer, substituteTemplatedText } = inject(
  formResponseInjectKey,
) as FormResponseState;

const answer = useAnswer<FormElementType.DISCUSSION>(
  props.modelValue,
  async (newVal, element, formResponse) => {
    // override the default api func logic
    const ans = discussionAnswerDto.value;
    await apiClient.formResponseAnswer.submitDiscussionAnswer(
      formResponse.guid,
      element.guid,
      element.repeaterGuid ?? emptyGuid,
      formResponse.formDefinitionGuid,
      ans,
    );

    return Promise.resolve({
      status: "updated",
      value: newVal,
    });
  },
);

const elementProperties = computed(() => props.modelValue.properties);

const currentUser = ref<UserInfoDto>({ ...myInfoStore.userInfo });
const newCommentText = ref("");
const authorColorMap = ref<Record<string, string>>({});
const discussionAnswerDto = ref<DiscussionAnswerDto>();
/** Flag to force update comments when editing or replying */
let forceUpdateComments = false;

const comments = computed<DiscussionComment[]>({
  get() {
    if (answer.value?.comments) {
      populateAuthorColorMap(answer.value.comments);
    }
    return answer.value?.comments ?? [];
  },
  set(newVal) {
    if (forceUpdateComments || !isEqual(answer.value?.comments, newVal)) {
      answer.value = {
        ...answer.value,
        comments: newVal,
      };
    }
    forceUpdateComments = false; // reset flag
  },
});

const isNewCommentEmpty = computed(() => isCommentEmpty(newCommentText.value));

const submitComment = () => {
  if (isNewCommentEmpty.value) {
    ElMessage({ message: "Comment cannot be empty", type: "warning" });
    return;
  }

  const newComment: DiscussionComment = {
    id: Date.now(),
    authorName: currentUser.value.loginName,
    authorGuid: currentUser.value.userGuid,
    text: newCommentText.value,
    createdAt: new Date().toISOString(),
    replies: [],
  };

  discussionAnswerDto.value = {
    id: newComment.id,
    text: newCommentText.value,
    replyTo: 0, // if this is a new comment, replyTo is 0 (no parent comment)
  };

  comments.value = [...comments.value, newComment]; // triggers setter

  newCommentText.value = "";
};

/**
 * This method is used to handle the reply to a comment, or edit the comment itself.
 *
 * If we are editing the comment, the id of the 'reply' will be the same as the 'parentOrReplyId'.
 *
 * @param parentOrReplyId The ID of the parent comment or the reply ID.
 * @param reply The reply object containing the text and other details.
 */
const handleReply = (parentOrReplyId: number, reply: DiscussionComment) => {
  const updated = cloneDeep(comments.value);

  discussionAnswerDto.value = {
    id: reply.id,
    text: reply.text,
    replyTo: parentOrReplyId, // This is the ID of the comment being replied to (or edited)
  };

  forceUpdateComments = true;
  comments.value = updated;
};

const populateAuthorColorMap = (comments: DiscussionComment[]) => {
  const colors = elementProperties.value.colors || [
    "#EF9A9A",
    "#A5D6A7",
    "#90CAF9",
    "#FFD54F",
    "#CE93D8",
  ];
  comments.forEach((comment) => {
    if (!authorColorMap.value[comment.authorGuid]) {
      const color =
        colors[Object.keys(authorColorMap.value).length % colors.length];
      authorColorMap.value[comment.authorGuid] = color;
    }
    if (comment.replies?.length) {
      populateAuthorColorMap(comment.replies);
    }
  });
};

onMounted(() => {
  populateAuthorColorMap(comments.value);
});

const inPdf = useIsInPdfMode();
</script>

<template id="form-discussion-component">
  <div class="DiscussionComponent">
    <slot name="label" :element="modelValue" />

    <div
      :style="{
        maxHeight: elementProperties.maxHeight || 'auto',
        overflowY: 'auto',
      }"
    >
      <DiscussionThreadComment
        v-for="comment in comments"
        :key="comment.id"
        :comment="comment"
        :color-hierarchy="
          elementProperties.colors || [
            '#000000',
            '#FF0000',
            '#00FF00',
            '#0000FF',
          ]
        "
        :author-color-map="authorColorMap"
        :current-user="currentUser"
        :current-user-guid="currentUser.userGuid"
        :properties="elementProperties"
        :read-only="props.isReadOnly"
        :current-depth="1"
        :substitute-templated-text="substituteTemplatedText"
        @reply="handleReply"
      />
    </div>
    <div v-if="!inPdf" class="reply-input">
      <DiscussionThreadEditor
        v-model="newCommentText"
        class="reply-input-area"
        :disabled="props.isReadOnly"
        :placeholder="
          substituteTemplatedText(elementProperties.placeholder) ?? ''
        "
      />

      <el-button
        class="post-btn"
        type="primary"
        :disabled="isNewCommentEmpty || props.isReadOnly"
        @click="submitComment"
      >
        {{ elementProperties.saveButtonText || "Post" }}
      </el-button>

      <el-button
        v-if="!isNewCommentEmpty"
        text
        class="cancel-btn"
        :disabled="props.isReadOnly"
        @click="newCommentText = ''"
      >
        {{ elementProperties.cancelButtonText || "Cancel" }}
      </el-button>
    </div>
  </div>
</template>

<style lang="less">
.DiscussionComponent {
  .reply-input {
    display: grid;
    // Separates the composer from the last comment so it reads as a new action
    // rather than another entry in the thread.
    margin-top: 12px;
    // The buttons sit in the first column, so it is sized to them; the editor
    // spans both. `min-content` here would collapse the editor to a sliver,
    // since TinyMCE's root reports a near-zero intrinsic width.
    grid-template-columns: max-content 1fr;
    grid-template-rows: auto auto;
    grid-template-areas:
      "input input"
      "post cancel";
    gap: 0.5rem;

    .reply-input-area {
      grid-area: input;
      // A grid item defaults to min-width:auto, which lets the editor's own
      // intrinsic width win over the track. Without this it stays narrow no
      // matter how wide the column is.
      min-width: 0;
      width: 100%;
    }
    .post-btn {
      grid-area: post;
    }
    .cancel-btn {
      grid-area: cancel;
      color: #888;
      margin: 0;
    }

    .post-btn,
    .cancel-btn {
      justify-self: start;
    }
  }
}
</style>
